import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { initializeEcho, subscribeToBusinessOrders, unsubscribeFromBusinessOrders } from '../utils/echoSetup';
import notificationSound from '../assets/audio/good_chilling_vibe.mp3';

/**
 * Custom hook to handle real-time order updates
 * Automatically subscribes to business order events when user is authenticated
 */
export const useRealtimeOrders = () => {
  const dispatch = useDispatch();
  const channelRef = useRef(null);
  const businessIdRef = useRef(null);

  // Get auth data from Redux
  const { user, token } = useSelector((state) => state.auth);

  // Get business ID from cookies or user object
  const businessId = Cookies.get('user-business-id') || user?.business?.id;
  const authToken = token || Cookies.get('token');

  useEffect(() => {
    // Only initialize if we have both token and business ID
    if (!authToken || !businessId) {
      console.log('⏸️ Real-time orders: Waiting for auth token and business ID');
      return;
    }

    // If business ID changed, unsubscribe from old one
    if (businessIdRef.current && businessIdRef.current !== businessId) {
      console.log('🔄 Business changed, resubscribing...');
      unsubscribeFromBusinessOrders(businessIdRef.current);
      channelRef.current = null;
    }

    businessIdRef.current = businessId;

    // Initialize Echo
    try {
      initializeEcho(authToken);
    } catch (error) {
      console.error('❌ Failed to initialize Echo:', error);
      return;
    }

    // Subscribe to business orders
    const handleOrderCreated = (event) => {
      console.log('🆕 New Order Event:', event);

      // Show toast notification
      toast.success(
        <div>
          <strong>New Order Received!</strong>
          <br />
          Order #{event?.order?.id}
        </div>,
        {
          position: 'top-right',
          autoClose: 8000,
        }
      );

      // Play notification sound
      const audio = new Audio(notificationSound);
      audio.play().catch((err) => {
        console.warn('Autoplay blocked:', err);
      });

      // Dispatch Redux action to update orders list (optional - implement as needed)
      // dispatch(addNewOrder(event.order));
    };

    const handleOrderUpdated = (event) => {
      console.log('🔄 Order Updated Event:', event);

      // Show toast notification
      toast.info(
        <div>
          <strong>Order Updated</strong>
          <br />
          Order #{event?.order?.id} - {event?.order?.status}
        </div>,
        {
          position: 'top-right',
          autoClose: 5000,
        }
      );

      // Dispatch Redux action to update order (optional - implement as needed)
      // dispatch(updateOrder(event.order));
    };

    channelRef.current = subscribeToBusinessOrders(businessId, {
      onOrderCreated: handleOrderCreated,
      onOrderUpdated: handleOrderUpdated,
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      if (businessIdRef.current) {
        unsubscribeFromBusinessOrders(businessIdRef.current);
        channelRef.current = null;
      }
    };
  }, [authToken, businessId, dispatch]);

  return {
    isConnected: !!channelRef.current,
    businessId: businessIdRef.current,
  };
};
